import React from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Chip,
} from '@mui/material';

// Helper function to format score nicely
const formatScore = (score) => {
    if (score % 1 === 0) {
        // If it's a whole number, return as integer
        return score.toString();
    } else if (score % 0.5 === 0) {
        // If it's a half number (like 1.5), show one decimal
        return score.toFixed(1);
    } else {
        // For other decimals, round to 2 decimal places
        return (Math.round(score * 100) / 100).toString();
    }
};

const TopicProgress = ({ 
    currentSection, 
    currentQuestionIndex, 
    completedSections, 
    totalSections,
    answers,
    allQuestions,
    history // Add history prop to show attempt count
}) => {
    // Calculate answered questions in current section
    const currentSectionQuestions = allQuestions?.filter(q => q.sectionId === currentSection?.id) || [];
    const answeredInCurrentSection = currentSectionQuestions.filter(q => 
        answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== ''
    ).length;
    
    const currentSectionTotalQuestions = currentSectionQuestions.length;
    
    // Calculate overall progress based on completed sections + progress in current section
    const currentSectionProgress = currentSectionTotalQuestions > 0 
        ? answeredInCurrentSection / currentSectionTotalQuestions 
        : 0;
    
    const overallProgress = totalSections > 0 
        ? Math.min(100, Math.round(((completedSections + currentSectionProgress) / totalSections) * 100))
        : 0;

    // Get attempt count and best score from history
    const totalAttempts = history?.total_attempts || 0;
    const bestScore = history?.submissions?.length > 0 ? 
        Math.max(...history.submissions
            .filter(s => s.percentage !== null)
            .map(s => s.percentage) || [0]
        ) : 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {currentSection?.title || 'Loading...'}
                </Typography>
                <Chip 
                    label={`Section ${completedSections + 1}/${totalSections}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Box>
            
            {currentSection?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {currentSection.description}
                </Typography>
            )}

            <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Question {currentQuestionIndex + 1} of {currentSectionTotalQuestions} in this section
                    </Typography>
                    {totalAttempts > 0 && bestScore > 0 && (
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                            Best: {formatScore(bestScore)}%
                        </Typography>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    Overall Progress: {overallProgress}%
                </Typography>
            </Box>
            
            <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                    },
                }}
            />
        </Box>
    );
};

export default TopicProgress;
